(function () {
  const addonVariantCache = {};

  const normalizeHandle = (value) => (value || '').toString().trim().toLowerCase().replace(/^\/+|\/+$/g, '');

  const normalizeTitle = (value) => (value || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');

  const handleizeValue = (value) => (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const firstResolvedValue = (promises) => new Promise((resolve) => {
    const safePromises = (promises || []).filter(Boolean);
    if (!safePromises.length) {
      resolve(null);
      return;
    }

    let pending = safePromises.length;
    let resolved = false;

    safePromises.forEach((promise) => {
      Promise.resolve(promise)
        .then((value) => {
          if (!resolved && value) {
            resolved = true;
            resolve(value);
          }
        })
        .catch(() => {})
        .finally(() => {
          pending -= 1;
          if (!resolved && pending <= 0) resolve(null);
        });
    });
  });

  const resolveVariantFromProductJSON = (productJson, skuHint, allowFallback = true) => {
    if (!productJson || !Array.isArray(productJson.variants) || !productJson.variants.length) return null;

    if (skuHint) {
      const wantedSku = skuHint.toString().trim().toLowerCase();
      const exactVariant = productJson.variants.find((variant) => (variant?.sku || '').toString().trim().toLowerCase() === wantedSku);
      if (exactVariant?.id && exactVariant.available !== false) return exactVariant.id;
      if (!allowFallback) return null;
    }

    const firstAvailableVariant = productJson.variants.find((variant) => variant?.id && variant.available !== false);
    return firstAvailableVariant?.id || productJson.variants[0]?.id || null;
  };

  const fetchProductJSON = async (handle) => {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return null;

    const key = `handle:${normalizedHandle}`;
    if (addonVariantCache[key]) return addonVariantCache[key];

    addonVariantCache[key] = fetch(`/products/${normalizedHandle}.js`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    return addonVariantCache[key];
  };

  const suggestProductHandles = async (query) => {
    const q = (query || '').toString().trim();
    if (!q) return [];

    const key = `suggest:${q.toLowerCase()}`;
    if (addonVariantCache[key]) return addonVariantCache[key];

    const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants,tag`;
    addonVariantCache[key] = fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => (data?.resources?.results?.products || []).map((product) => product?.handle).filter(Boolean))
      .catch(() => []);

    return addonVariantCache[key];
  };

  const fetchAccessoriesProducts = async () => {
    const key = 'collection:accessories';
    if (addonVariantCache[key]) return addonVariantCache[key];

    addonVariantCache[key] = fetch('/collections/accessories/products.json?limit=250', {
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => (Array.isArray(data?.products) ? data.products : []))
      .catch(() => []);

    return addonVariantCache[key];
  };

  const fetchMerchProducts = async () => {
    const key = 'collection:merch';
    if (addonVariantCache[key]) return addonVariantCache[key];

    addonVariantCache[key] = fetch('/collections/merch/products.json?limit=250', {
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => (Array.isArray(data?.products) ? data.products : []))
      .catch(() => []);

    return addonVariantCache[key];
  };

  const getAvailableVariantId = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const firstAvailableVariant = variants.find((variant) => variant?.id && variant.available !== false);
    return firstAvailableVariant?.id || variants[0]?.id || null;
  };

  const resolveVariantFromAccessoriesCollection = async ({ skuHint, handleHints = [], queryCandidates = [], optionText = '', addonType = '', optionValue = '' }) => {
    const products = await fetchAccessoriesProducts();
    if (!Array.isArray(products) || !products.length) return null;

    const normalizedSku = (skuHint || '').toString().trim().toLowerCase();
    if (normalizedSku) {
      for (const product of products) {
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const exactVariant = variants.find((variant) => (variant?.sku || '').toString().trim().toLowerCase() === normalizedSku && variant.available !== false);
        if (exactVariant?.id) return exactVariant.id;
      }
    }

    const titleHints = [optionText, ...queryCandidates].map(normalizeTitle).filter(Boolean);
    const handleCandidates = [
      ...handleHints,
      handleizeValue(optionText),
      ...queryCandidates.map(handleizeValue),
      addonType,
      optionValue
    ].map(normalizeHandle).filter(Boolean);
    const uniqueHandleCandidates = [...new Set(handleCandidates)];

    for (const handleCandidate of uniqueHandleCandidates) {
      const matchingProduct = products.find((product) => normalizeHandle(product?.handle || '') === handleCandidate);
      const variantId = getAvailableVariantId(matchingProduct);
      if (variantId) return variantId;
    }

    const titleIncludesAny = (product) => {
      const productTitle = normalizeTitle(product?.title || '');
      if (!productTitle) return false;
      return titleHints.some((hint) => {
        if (!hint) return false;
        if (productTitle === hint) return true;
        if (productTitle.includes(hint) || hint.includes(productTitle)) return true;
        const hintTokens = hint.split(' ').filter(Boolean);
        return hintTokens.length > 1 && hintTokens.every((token) => productTitle.includes(token));
      });
    };

    let filteredProducts = products.filter((product) => titleIncludesAny(product));

    if (!filteredProducts.length && addonType === 'tpms') {
      filteredProducts = products.filter((product) => {
        const productTitle = normalizeTitle(product?.title || '');
        return productTitle.includes('tpms') || productTitle.includes('tire pressure');
      });
    }

    if (!filteredProducts.length && addonType === 'lugkit') {
      const normalizedOptionText = normalizeTitle(optionText);
      filteredProducts = products.filter((product) => {
        const productTitle = normalizeTitle(product?.title || '');
        if (!productTitle) return false;
        const hasLugKeyword = productTitle.includes('lug');
        const hasSplineKeyword = normalizedOptionText.includes('spline') ? productTitle.includes('spline') : true;
        const hasSpikeKeyword = normalizedOptionText.includes('spike') ? productTitle.includes('spike') : true;
        const hasChromeKeyword = normalizedOptionText.includes('chrome') ? productTitle.includes('chrome') : true;
        const hasBlackKeyword = normalizedOptionText.includes('black') ? productTitle.includes('black') : true;
        return hasLugKeyword && hasSplineKeyword && hasSpikeKeyword && hasChromeKeyword && hasBlackKeyword;
      });
    }

    if (!filteredProducts.length && addonType === 'lugwrench') {
      const normalizedOptionText = normalizeTitle(optionText);
      filteredProducts = products.filter((product) => {
        const productTitle = normalizeTitle(product?.title || '');
        if (!productTitle) return false;
        if (normalizedOptionText.includes('gorilla')) return productTitle.includes('gorilla') || productTitle.includes('tw705');
        return productTitle.includes('lug') && productTitle.includes('wrench');
      });
    }

    const exactTitleMatch = filteredProducts.find((product) => normalizeTitle(product?.title || '') === normalizeTitle(optionText));
    const exactVariantId = getAvailableVariantId(exactTitleMatch);
    if (exactVariantId) return exactVariantId;

    const prioritizedProduct = filteredProducts.find((product) => {
      const productTitle = normalizeTitle(product?.title || '');
      if (addonType === 'tpms') return productTitle.includes('tpms') || productTitle.includes('sensor');
      if (addonType === 'lugkit') return productTitle.includes('lug');
      if (addonType === 'lugwrench') return productTitle.includes('wrench') || productTitle.includes('tw705');
      return true;
    }) || filteredProducts[0];

    return getAvailableVariantId(prioritizedProduct);
  };

  const getOptionContext = (select, option) => {
    const optionValue = (option.value || '').toString().trim().toLowerCase();
    const addonType = (option.dataset.addonType || select.dataset.addonType || '').toString().trim().toLowerCase();
    const optionText = (option.textContent || '').replace(/\s*\$[\d,]+(?:\.\d{2})?.*$/g, '').trim();

    const builtInFallbacks = {
      tpms: {
        handles: ['tpms', 'tpms-sensor', 'tpms-sensors', 'tire-pressure-monitoring-system', 'tire-pressure-monitoring-system-sensors'],
        queries: ['TPMS', 'TPMS Sensors', 'Tire Pressure Monitoring System']
      },
      'lugkit-chrome-spline': {
        handles: ['chrome-spline-lugs', 'chrome-spline-lug-kit', 'chrome-spline-lug-kits', 'chrome-spline-lug-nuts'],
        queries: ['Chrome Spline Lugs', 'Chrome Spline Lug Kit']
      },
      'lugkit-black-spline': {
        handles: ['black-spline-lugs', 'black-spline-lug-kit', 'black-spline-lug-kits', 'black-spline-lug-nuts'],
        queries: ['Black Spline Lugs', 'Black Spline Lug Kit']
      },
      'lugkit-chrome-spike': {
        handles: ['chrome-spike-lugs', 'chrome-spike-lug-kit', 'chrome-spike-lug-kits', 'chrome-spike-lug-nuts'],
        queries: ['Chrome Spike Lugs', 'Chrome Spike Lug Kit']
      },
      'lugkit-black-spike': {
        handles: ['black-spike-lugs', 'black-spike-lug-kit', 'black-spike-lug-kits', 'black-spike-lug-nuts'],
        queries: ['Black Spike Lugs', 'Black Spike Lug Kit']
      },
      'lugwrench-standard': {
        handles: ['lug-wrench', 'lug-wrench-kit'],
        queries: ['Lug Wrench']
      },
      'lugwrench-gorilla': {
        handles: ['gorilla-torque-wrench', 'gorilla-torque-wrench-tw705', 'gorilla-tw705', 'tw705'],
        queries: ['Gorilla TW705', 'Gorilla Torque Wrench']
      }
    };

    const builtInFallback = builtInFallbacks[optionValue] || builtInFallbacks[addonType] || null;
    const handleHint = option.dataset.addonFallbackHandle || option.dataset.handle || select.dataset.addonFallbackHandle || select.dataset.handle || '';
    const handleHintsRaw = option.dataset.addonFallbackHandles || select.dataset.addonFallbackHandles || '';
    const extraHandleHints = handleHintsRaw ? handleHintsRaw.split(',').map(normalizeHandle).filter(Boolean) : [];
    const queryHint = option.dataset.addonFallbackQuery || select.dataset.addonFallbackQuery || '';
    const skuHint = option.dataset.addonSku || select.dataset.addonSku || '';

    const hintedHandles = [
      normalizeHandle(handleHint),
      ...extraHandleHints,
      ...((builtInFallback?.handles || []).map(normalizeHandle))
    ].filter(Boolean);

    const uniqueHintedHandles = [...new Set(hintedHandles)];
    const queryCandidates = [queryHint, skuHint, optionText, ...(builtInFallback?.queries || [])].filter(Boolean);

    return { optionValue, addonType, optionText, skuHint, uniqueHintedHandles, queryCandidates };
  };

  const resolveVariantId = async (select, option) => {
    const existingId = option.dataset.addonVariantId || option.dataset.variantId || '';
    if (/^\d+$/.test(existingId)) return existingId;

    const optionCacheKey = `option:${select.id || select.dataset.addonType || ''}:${option.value || ''}`;
    if (addonVariantCache[optionCacheKey]) return addonVariantCache[optionCacheKey];

    addonVariantCache[optionCacheKey] = (async () => {
      const { optionValue, addonType, optionText, skuHint, uniqueHintedHandles, queryCandidates } = getOptionContext(select, option);

      const tryResolveFromHandle = async (handle, allowFallback = false) => {
        const productJson = await fetchProductJSON(handle);
        const resolved = resolveVariantFromProductJSON(productJson, skuHint, allowFallback);
        return resolved && /^\d+$/.test(String(resolved)) ? resolved : null;
      };

      const resolvedFromHints = await firstResolvedValue(uniqueHintedHandles.map((hintedHandle) => tryResolveFromHandle(hintedHandle, !skuHint)));
      if (resolvedFromHints) return resolvedFromHints;

      const suggestedHandleLists = await Promise.all(queryCandidates.map((query) => suggestProductHandles(query)));
      const candidateHandles = suggestedHandleLists.flat();

      const seenHandles = new Set();
      const uniqueCandidateHandles = candidateHandles
        .map(normalizeHandle)
        .filter((normalizedHandle) => {
          if (!normalizedHandle || seenHandles.has(normalizedHandle) || uniqueHintedHandles.includes(normalizedHandle)) return false;
          seenHandles.add(normalizedHandle);
          return true;
        });

      const resolvedFromSuggestedHandles = await firstResolvedValue(uniqueCandidateHandles.map((handle) => tryResolveFromHandle(handle, !skuHint)));
      if (resolvedFromSuggestedHandles) return resolvedFromSuggestedHandles;

      const resolvedFromAccessories = await resolveVariantFromAccessoriesCollection({
        skuHint,
        handleHints: uniqueHintedHandles,
        queryCandidates,
        optionText,
        addonType,
        optionValue
      });
      if (resolvedFromAccessories) return resolvedFromAccessories;

      if (skuHint) {
        const resolvedWithFallback = await firstResolvedValue([
          ...uniqueHintedHandles.map((handle) => tryResolveFromHandle(handle, true)),
          ...uniqueCandidateHandles.map((handle) => tryResolveFromHandle(handle, true))
        ]);
        if (resolvedWithFallback) return resolvedWithFallback;
      }

      return null;
    })();

    const resolvedVariantId = await addonVariantCache[optionCacheKey];
    if (resolvedVariantId) {
      option.dataset.addonVariantId = String(resolvedVariantId);
      option.dataset.variantId = String(resolvedVariantId);
    }
    return resolvedVariantId;
  };

  const warmAddonOption = (select, option) => {
    if (!option || !option.value || option.disabled) return Promise.resolve(null);
    const existingId = option.dataset.addonVariantId || option.dataset.variantId || '';
    if (/^\d+$/.test(existingId)) return Promise.resolve(existingId);
    return resolveVariantId(select, option).catch(() => null);
  };

  const warmAllAddonOptions = (selects) => {
    fetchAccessoriesProducts();

    const optionsToWarm = [];
    selects.forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption?.value) optionsToWarm.push({ select, option: selectedOption });
      Array.from(select.options || []).forEach((option) => {
        if (option?.value) optionsToWarm.push({ select, option });
      });
    });

    const uniqueOptions = [];
    const seen = new Set();
    optionsToWarm.forEach(({ select, option }) => {
      const key = `${select.id || select.dataset.addonType || ''}:${option.value || ''}`;
      if (seen.has(key)) return;
      seen.add(key);
      uniqueOptions.push({ select, option });
    });

    uniqueOptions.forEach(({ select, option }) => warmAddonOption(select, option));
  };

  const initCartAddons = () => {
    document.querySelectorAll('[data-cart-addons]').forEach((container) => {
      if (container.dataset.cartAddonsBound === 'true') return;
      container.dataset.cartAddonsBound = 'true';

      const selects = Array.from(container.querySelectorAll('[data-cart-addon-select]'));
      const addButton = container.querySelector('[data-cart-addons-add]');
      const message = container.querySelector('[data-cart-addons-message]');
      if (!selects.length || !addButton) return;

      const setMessage = (text, isError) => {
        if (!message) return;
        message.textContent = text || '';
        message.hidden = !text;
        message.classList.toggle('cart-addons__message--error', Boolean(isError));
      };

      const updateButton = () => {
        const hasSelection = selects.some((select) => Boolean(select.value));
        addButton.hidden = !hasSelection;
        addButton.disabled = !hasSelection;
        if (hasSelection) setMessage('', false);
      };

      selects.forEach((select) => {
        select.addEventListener('change', () => {
          updateButton();
          const selectedOption = select.options[select.selectedIndex];
          warmAddonOption(select, selectedOption);
        });
        select.addEventListener('focus', () => warmAllAddonOptions([select]), { once: true });
        select.addEventListener('pointerenter', () => warmAllAddonOptions([select]), { once: true });
      });

      addButton.addEventListener('click', async () => {
        const selectedOptions = selects
          .filter((select) => Boolean(select.value))
          .map((select) => ({ select, option: select.options[select.selectedIndex] }))
          .filter((entry) => entry.option);

        if (!selectedOptions.length) return;

        addButton.disabled = true;
        addButton.classList.add('loading');
        setMessage('Adding selected items...', false);

        const resolvedItems = await Promise.all(selectedOptions.map(async ({ select, option }) => {
          const variantId = await resolveVariantId(select, option);
          const quantity = Math.max(parseInt(option.dataset.addonQuantity || option.dataset.quantity || option.dataset.qty || '1', 10), 1);
          return variantId ? { id: Number(variantId), quantity } : null;
        }));

        const items = resolvedItems.filter(Boolean);

        if (!items.length) {
          addButton.disabled = false;
          addButton.classList.remove('loading');
          setMessage('Unable to add the selected item. Please refresh and try again.', true);
          return;
        }

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ items })
          });

          if (!response.ok) throw new Error('Cart add failed');
          window.location.href = '/cart';
        } catch (error) {
          addButton.disabled = false;
          addButton.classList.remove('loading');
          setMessage('Unable to add the selected item. Please refresh and try again.', true);
        }
      });

      updateButton();

      const warmNow = () => warmAllAddonOptions(selects);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warmNow, { timeout: 1200 });
      } else {
        window.setTimeout(warmNow, 250);
      }
    });
  };



  const formatCartMoney = (cents) => {
    const value = Number(cents || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(value);
      } catch (error) {}
    }
    return `$${(value / 100).toFixed(2)}`;
  };

  const getProductImageUrl = (product) => {
    if (!product) return '';
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image?.src) return product.featured_image.src;
    if (Array.isArray(product.images) && product.images.length) {
      const firstImage = product.images[0];
      return typeof firstImage === 'string' ? firstImage : firstImage?.src || '';
    }
    if (product.image?.src) return product.image.src;
    return '';
  };

  const variantSizeLabel = (variant) => {
    const title = (variant?.title || '').toString().trim();
    const option1 = (variant?.option1 || '').toString().trim();
    const option2 = (variant?.option2 || '').toString().trim();
    const option3 = (variant?.option3 || '').toString().trim();
    const firstUsableOption = [option1, option2, option3].find((value) => value && value.toLowerCase() !== 'default title');
    return firstUsableOption || (title && title.toLowerCase() !== 'default title' ? title : 'One Size');
  };

  const productLooksLikeDuallyShirt = (product) => {
    const title = normalizeTitle(product?.title || '');
    const handle = normalizeHandle(product?.handle || '');
    const type = normalizeTitle(product?.product_type || '');
    const haystack = `${title} ${handle} ${type}`;
    const hasShirtWord = haystack.includes('shirt') || haystack.includes('t-shirt') || haystack.includes('tee');
    const hasDuallyWord = haystack.includes('dually') || haystack.includes('duallys') || haystack.includes('dually-s') || haystack.includes('dually-only');
    return hasShirtWord && hasDuallyWord;
  };

  const productLooksLikeDuallyHat = (product) => {
    const title = normalizeTitle(product?.title || '');
    const handle = normalizeHandle(product?.handle || '');
    const type = normalizeTitle(product?.product_type || '');
    const haystack = `${title} ${handle} ${type}`;
    const hasHatWord = haystack.includes('hat') || haystack.includes('snapback') || haystack.includes('snap back') || haystack.includes('richardson') || haystack.includes('cap');
    const hasDuallyWord = haystack.includes('dually') || haystack.includes('duallys') || haystack.includes('dually-s') || haystack.includes('dually-only');
    return hasHatWord && hasDuallyWord;
  };

  const productMatchesShirtRequest = (product, query, displayTitle) => {
    const haystack = normalizeTitle(`${product?.title || ''} ${product?.handle || ''} ${product?.product_type || ''}`);
    const request = normalizeTitle(`${query || ''} ${displayTitle || ''}`);
    if (!request) return productLooksLikeDuallyShirt(product);

    const wantsHat = request.includes('hat') || request.includes('snapback') || request.includes('snap back') || request.includes('richardson') || request.includes('cap');
    if (wantsHat) return productLooksLikeDuallyHat(product);

    const wantsVertical = request.includes('vertical') || request.includes('square');
    const productIsVertical = haystack.includes('vertical') || haystack.includes('square');

    if (wantsVertical) return productIsVertical && (haystack.includes('shirt') || haystack.includes('tee'));
    if (request.includes('t shirt') || request.includes('t-shirt') || request.includes('tee') || request.includes('shirt')) {
      return !productIsVertical && productLooksLikeDuallyShirt(product);
    }
    return productLooksLikeDuallyShirt(product);
  };

  const resolveShirtProduct = async (initialHandle, query, displayTitle, altHandlesString) => {
    const requestQuery = query || displayTitle || '';
    const altHandles = String(altHandlesString || '')
      .split(',')
      .map((handle) => normalizeHandle(handle))
      .filter(Boolean);

    const directHandles = [
      initialHandle,
      ...altHandles,
      'duallys-only-t-shirt-white-logo',
      'duallys-only-white-logo-t-shirt',
      'duallys-only-t-shirt-white-logo-horizontal',
      'duallys-only-horizontal-logo-t-shirt',
      'duallys-only-horizontal-t-shirt',
      'duallys-only-t-shirt',
      'duallys-only-tshirt',
      'duallys-only-tshirts',
      'duallys-only-tee',
      'duallys-only-tees',
      'duallys-only-shirt',
      'duallys-only-logo-t-shirt',
      'duallys-only-logo-tee',
      'duallys-only-brand-tee',
      'duallys-only-brand-t-shirt',
      'duallys-only-black-t-shirt',
      'duallys-only-black-tee',
      'duallys-only-vertical',
      'duallys-only-vertical-t-shirt',
      'duallys-only-square-logo-t-shirt',
      'duallys-only-square-t-shirt',
      'duallys-only-vertical-logo-t-shirt',
      'duallys-only-black-white-logo-richardson-112-snap-back-hat',
      'duallys-only-black-white-logo-richardson-112-snapback-hat',
      'duallys-only-richardson-112-snap-back-hat',
      'duallys-only-richardson-112-snapback-hat',
      'duallys-only-snap-back-hat',
      'duallys-only-snapback-hat',
      'duallys-only-hat'
    ].map(normalizeHandle).filter(Boolean);

    const uniqueDirectHandles = [...new Set(directHandles)];
    for (const handle of uniqueDirectHandles) {
      const product = await fetchProductJSON(handle);
      if (product && Array.isArray(product.variants) && product.variants.length && productMatchesShirtRequest(product, requestQuery, displayTitle)) return product;
    }

    for (const handle of uniqueDirectHandles) {
      const product = await fetchProductJSON(handle);
      if (product && Array.isArray(product.variants) && product.variants.length && handle === normalizeHandle(initialHandle)) return product;
    }

    const merchProducts = await fetchMerchProducts();
    const wantsHat = normalizeTitle(`${requestQuery || ''} ${displayTitle || ''}`).includes('hat') || normalizeTitle(`${requestQuery || ''} ${displayTitle || ''}`).includes('snapback') || normalizeTitle(`${requestQuery || ''} ${displayTitle || ''}`).includes('snap back') || normalizeTitle(`${requestQuery || ''} ${displayTitle || ''}`).includes('richardson');
    const matchingMerchProduct = merchProducts.find((product) => productMatchesShirtRequest(product, requestQuery, displayTitle))
      || (wantsHat ? merchProducts.find(productLooksLikeDuallyHat) : merchProducts.find(productLooksLikeDuallyShirt))
      || merchProducts.find((product) => {
        const haystack = normalizeTitle(`${product?.title || ''} ${product?.handle || ''} ${product?.product_type || ''}`);
        return wantsHat ? (haystack.includes('hat') || haystack.includes('snapback') || haystack.includes('richardson') || haystack.includes('cap')) : (haystack.includes('shirt') || haystack.includes('tee'));
      });

    if (matchingMerchProduct?.handle) {
      const product = await fetchProductJSON(matchingMerchProduct.handle);
      if (product && Array.isArray(product.variants) && product.variants.length) return product;
    }

    const suggestedQueries = [
      requestQuery,
      displayTitle,
      "Dually's Only T-Shirt",
      'Duallys Only T-Shirt',
      "Dually's Only Tee",
      'Duallys Only Tee',
      'Duallys Only Shirt',
      'Duallys Only Snapback Hat',
      "Dually's Only Snapback Hat",
      'Duallys Only Richardson 112 Hat',
      'Duallys Only Hat',
      'T-Shirt',
      'Snapback Hat'
    ].filter(Boolean);

    const suggestedHandleLists = await Promise.all(suggestedQueries.map((searchQuery) => suggestProductHandles(searchQuery)));
    const candidateHandles = [...new Set(suggestedHandleLists.flat().map(normalizeHandle).filter(Boolean))];
    for (const handle of candidateHandles) {
      const product = await fetchProductJSON(handle);
      if (product && Array.isArray(product.variants) && product.variants.length && productMatchesShirtRequest(product, requestQuery, displayTitle)) return product;
    }

    for (const handle of candidateHandles) {
      const product = await fetchProductJSON(handle);
      if (product && Array.isArray(product.variants) && product.variants.length) return product;
    }

    return null;
  };

  const populateShirtUpsell = (container, product) => {
    const select = container.querySelector('[data-cart-shirt-select]');
    const title = container.querySelector('[data-cart-shirt-title]');
    const image = container.querySelector('[data-cart-shirt-image]');
    const price = container.querySelector('[data-cart-shirt-price]');
    if (!select || !product) return false;

    const displayTitle = container.dataset.shirtDisplayTitle || product.title || 'Duallys Only T-Shirt';
    const selectLabel = container.dataset.shirtSelectLabel || 'Select Shirt Size';
    if (title) title.textContent = displayTitle;

    const imageUrl = getProductImageUrl(product);
    if (imageUrl && image) {
      image.src = imageUrl;
      image.alt = displayTitle;
    }

    const availableVariants = (product.variants || []).filter((variant) => variant?.id && variant.available !== false);
    if (!availableVariants.length) return false;

    select.innerHTML = `<option value="">${selectLabel}</option>`;
    availableVariants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = String(variant.id);
      option.dataset.shirtPrice = formatCartMoney(variant.price);
      option.textContent = `${variantSizeLabel(variant)} - ${formatCartMoney(variant.price)}`;
      select.appendChild(option);
    });

    if (price) price.textContent = formatCartMoney(availableVariants[0].price);
    container.dataset.shirtLoaded = 'true';
    container.removeAttribute('data-cart-shirt-loading');
    return true;
  };

  const initCartShirtUpsell = () => {
    document.querySelectorAll('[data-cart-shirt-upsell]').forEach((container) => {
      if (container.dataset.cartShirtBound === 'true') return;
      container.dataset.cartShirtBound = 'true';

      const select = container.querySelector('[data-cart-shirt-select]');
      const addButton = container.querySelector('[data-cart-shirt-add]');
      const message = container.querySelector('[data-cart-shirt-message]');
      const price = container.querySelector('[data-cart-shirt-price]');
      if (!select || !addButton) return;

      const setMessage = (text, isError) => {
        if (!message) return;
        message.textContent = text || '';
        message.hidden = !text;
        message.classList.toggle('cart-shirt-upsell__message--error', Boolean(isError));
      };

      const updateButton = () => {
        const selectedOption = select.options[select.selectedIndex];
        const hasSelection = Boolean(select.value);
        addButton.hidden = !hasSelection;
        addButton.disabled = !hasSelection;
        if (selectedOption?.dataset?.shirtPrice && price) price.textContent = selectedOption.dataset.shirtPrice;
        if (hasSelection) setMessage('', false);
      };

      const loadProduct = async () => {
        if (container.dataset.shirtLoaded === 'true') return;
        const product = await resolveShirtProduct(container.dataset.shirtHandle || '', container.dataset.shirtQuery || '', container.dataset.shirtDisplayTitle || '', container.dataset.shirtAltHandles || '');
        const itemLabel = container.dataset.shirtItemLabel || 'shirt';
        if (!product) {
          setMessage(`${itemLabel.charAt(0).toUpperCase()}${itemLabel.slice(1)} options are unavailable right now.`, true);
          return;
        }
        if (!populateShirtUpsell(container, product)) {
          setMessage(`${itemLabel.charAt(0).toUpperCase()}${itemLabel.slice(1)} options are unavailable right now.`, true);
        }
        updateButton();
      };

      select.addEventListener('change', updateButton);
      select.addEventListener('focus', loadProduct, { once: true });
      select.addEventListener('pointerenter', loadProduct, { once: true });

      addButton.addEventListener('click', async () => {
        if (!select.value) return;
        addButton.disabled = true;
        addButton.classList.add('loading');
        const itemLabel = container.dataset.shirtItemLabel || 'shirt';
        setMessage(`Adding ${itemLabel}...`, false);

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ items: [{ id: Number(select.value), quantity: 1 }] })
          });

          if (!response.ok) throw new Error('Cart add failed');
          window.location.href = '/cart';
        } catch (error) {
          addButton.disabled = false;
          addButton.classList.remove('loading');
          const itemLabel = container.dataset.shirtItemLabel || 'shirt';
          setMessage(`Unable to add the ${itemLabel}. Please refresh and try again.`, true);
        }
      });

      updateButton();
      if (container.dataset.shirtLoaded !== 'true') {
        container.setAttribute('data-cart-shirt-loading', 'true');
        loadProduct().catch(() => {});
      }
    });
  };

  const initCartUpsells = () => {
    initCartAddons();
    initCartShirtUpsell();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartUpsells);
  } else {
    initCartUpsells();
  }

  document.addEventListener('shopify:section:load', initCartUpsells);
})();
